import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Tag, Plus, X, Edit, Trash2, AlertTriangle, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/inventario/categorias/`;

const INPUT_CLS = 'campo';

export default function CategoriasManager() {
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState(null);

    // Modal crear/editar
    const [formModal, setFormModal]   = useState(false);
    const [formData, setFormData]     = useState({ id: null, nombre: '', descripcion: '', activa: true });

    // Modal confirmar eliminación
    const [deleteModal, setDeleteModal] = useState(null); // { id, nombre, total_productos }
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError]     = useState(null);

    const fetchCategorias = async () => {
        try {
            const res = await axios.get(API);
            setCategorias(res.data);
        } catch {
            setError('Error al cargar categorías.');
        }
    };

    useEffect(() => { fetchCategorias(); }, []);

    // ── CREAR / EDITAR ─────────────────────────────────────────────────────────
    const openForm = (cat = null) => {
        setFormData(cat
            ? { id: cat.id, nombre: cat.nombre, descripcion: cat.descripcion || '', activa: cat.activa }
            : { id: null, nombre: '', descripcion: '', activa: true }
        );
        setError(null);
        setFormModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (formData.id) {
                await axios.put(`${API}${formData.id}/`, formData);
            } else {
                await axios.post(API, formData);
            }
            setFormModal(false);
            fetchCategorias();
        } catch (err) {
            setError(err.response?.data?.nombre?.[0] || 'Error al guardar la categoría.');
        } finally {
            setLoading(false);
        }
    };

    // ── ELIMINAR ───────────────────────────────────────────────────────────────
    const openDelete = (cat) => {
        setDeleteModal({ id: cat.id, nombre: cat.nombre, total_productos: cat.total_productos ?? 0 });
        setDeleteError(null);
    };

    const handleDelete = async () => {
        if (!deleteModal) return;
        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await axios.delete(`${API}${deleteModal.id}/`);
            setDeleteModal(null);
            fetchCategorias();
        } catch (err) {
            setDeleteError(err.response?.data?.detail || 'No se pudo eliminar la categoría.');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center bg-white dark:bg-slate-900/60 backdrop-blur-sm p-6 rounded-2xl border border-slate-200 dark:border-white/10">
                <div className="flex items-center gap-4">
                    <div className="bg-brand-500/20 p-3 rounded-xl">
                        <Tag size={24} className="text-brand-800 dark:text-brand-400" />
                    </div>
                    <div>
                        <h2 className="titulo-modal">Categorías de Productos</h2>
                        <p className="text-sm text-slate-500 dark:text-gray-400">{categorias.length} categorías registradas</p>
                    </div>
                </div>
                <button
                    onClick={() => openForm()}
                    className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-exito-500/30 hover:-translate-y-0.5"
                >
                    <Plus size={18} /> Nueva Categoría
                </button>
            </div>

            {error && !formModal && (
                <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-4 rounded-xl">{error}</div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categorias.map(cat => (
                    <div
                        key={cat.id}
                        className="bg-white dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl p-5 hover:border-exito-500/40 hover:shadow-md transition-all"
                    >
                        <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${cat.activa ? 'bg-exito-400' : 'bg-error-400'}`} />
                                <h3 className="font-bold text-slate-900 dark:text-white text-lg truncate">{cat.nombre}</h3>
                            </div>
                            {/* Botones siempre visibles */}
                            <div className="flex gap-1 shrink-0 ml-2">
                                <button
                                    onClick={() => openForm(cat)}
                                    title="Editar categoría"
                                    className="p-1.5 rounded-lg text-slate-400 hover:bg-brand-500/20 hover:text-brand-800 dark:hover:text-brand-800 transition-colors"
                                >
                                    <Edit size={15} />
                                </button>
                                <button
                                    onClick={() => openDelete(cat)}
                                    title="Eliminar categoría"
                                    className="p-1.5 rounded-lg text-slate-400 hover:bg-error-500/20 hover:text-error-500 dark:hover:text-error-400 transition-colors"
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        </div>

                        {cat.descripcion && (
                            <p className="text-sm text-slate-500 dark:text-gray-400 mb-3 line-clamp-2">{cat.descripcion}</p>
                        )}

                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-white/5">
                            <span className="flex items-center gap-1.5 text-sm text-slate-400 dark:text-gray-500">
                                <Package size={13} />
                                {cat.total_productos} producto{cat.total_productos !== 1 ? 's' : ''}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${cat.activa ? 'bg-exito-500/20 text-exito-500 dark:text-exito-400' : 'bg-error-500/20 text-error-500 dark:text-error-400'}`}>
                                {cat.activa ? 'Activa' : 'Inactiva'}
                            </span>
                        </div>
                    </div>
                ))}

                {categorias.length === 0 && (
                    <div className="col-span-3 text-center py-16 text-slate-400 dark:text-gray-500">
                        <Tag size={48} className="mx-auto mb-4 opacity-30" />
                        <p>No hay categorías. Creá la primera.</p>
                    </div>
                )}
            </div>

            {/* ── Modal: Crear / Editar ─────────────────────────────────────────────── */}
            <AnimatePresence>
                {formModal && (
                    <div className="fondo-modal">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-md shadow-2xl"
                        >
                            <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-white/10">
                                <h3 className="titulo-modal">
                                    <Tag className="text-brand-800 dark:text-brand-400" size={20} />
                                    {formData.id ? 'Editar Categoría' : 'Nueva Categoría'}
                                </h3>
                                <button onClick={() => setFormModal(false)} className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                {error && (
                                    <div className="bg-error-500/10 border border-error-500/30 text-error-400 p-3 rounded-xl text-sm">{error}</div>
                                )}

                                <div>
                                    <label htmlFor="categoria-nombre" className="etiqueta-campo normal-case text-sm">Nombre *</label>
                                    <input id="categoria-nombre"
                                        required
                                        type="text"
                                        className={INPUT_CLS}
                                        placeholder="Ej: Herramientas Manuales"
                                        value={formData.nombre}
                                        onChange={e => {
                                            const val = e.target.value;
                                            const cursor = e.target.selectionStart;
                                            setFormData(p => ({ ...p, nombre: val.replace(/\b\w/g, c => c.toUpperCase()) }));
                                            requestAnimationFrame(() => e.target.setSelectionRange(cursor, cursor));
                                        }}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="categoria-descripcion" className="etiqueta-campo normal-case text-sm">Descripción</label>
                                    <textarea id="categoria-descripcion"
                                        rows={3}
                                        className={`${INPUT_CLS} resize-none`}
                                        placeholder="Descripción opcional..."
                                        value={formData.descripcion}
                                        onChange={e => setFormData(p => ({ ...p, descripcion: e.target.value }))}
                                    />
                                </div>

                                <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-white/5 rounded-xl">
                                    <label htmlFor="categoria-activa" className="text-sm font-semibold text-slate-600 dark:text-gray-300 flex-1">Categoría Activa</label>
                                    <button
                                        type="button"
                                        id="categoria-activa"
                                        role="switch"
                                        aria-checked={formData.activa}
                                        onClick={() => setFormData(p => ({ ...p, activa: !p.activa }))}
                                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${formData.activa ? 'bg-exito-500' : 'bg-slate-300 dark:bg-gray-600'}`}
                                    >
                                        <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.activa ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>

                                <div className="pie-modal">
                                    <button
                                        type="button"
                                        onClick={() => setFormModal(false)}
                                        className="px-5 py-2.5 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-brand-500 hover:bg-brand-400 text-negro-950 px-6 py-2.5 rounded-xl font-semibold disabled:opacity-50 transition-all"
                                    >
                                        {loading ? 'Guardando...' : (formData.id ? 'Guardar Cambios' : 'Crear Categoría')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── Modal: Confirmar eliminación ──────────────────────────────────────── */}
            <AnimatePresence>
                {deleteModal && (
                    <div className="fondo-modal">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/20 rounded-3xl w-full max-w-sm shadow-2xl p-6 sm:p-8"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <div className="bg-error-100 dark:bg-error-500/15 p-3 rounded-xl">
                                    <Trash2 size={22} className="text-error-500" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-800 dark:text-white">Eliminar Categoría</h3>
                                    <p className="text-xs font-bold text-error-500">"{deleteModal.nombre}"</p>
                                </div>
                            </div>

                            {deleteModal.total_productos > 0 ? (
                                <div className="bg-alerta-50 dark:bg-alerta-500/10 border border-alerta-200 dark:border-alerta-500/20 rounded-2xl p-4 mb-6">
                                    <div className="flex gap-2 items-start">
                                        <AlertTriangle size={16} className="text-alerta-500 shrink-0 mt-0.5" />
                                        <div className="text-sm text-alerta-700 dark:text-alerta-300 font-medium space-y-1">
                                            <p className="font-black">
                                                {deleteModal.total_productos} producto{deleteModal.total_productos !== 1 ? 's' : ''} quedarán sin categoría
                                            </p>
                                            <p className="text-xs font-normal">
                                                Podés reasignarlos desde el módulo de Productos antes de eliminar, o eliminar igualmente.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                                    Esta categoría no tiene productos asignados. La eliminación es segura.
                                </p>
                            )}

                            {deleteError && (
                                <div className="bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-600 dark:text-error-400 text-sm p-3 rounded-xl mb-4">
                                    {deleteError}
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteModal(null)}
                                    className="flex-1 px-4 py-3 rounded-2xl border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteLoading}
                                    className="flex-1 bg-error-600 hover:bg-error-700 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs disabled:opacity-50 transition-colors"
                                >
                                    {deleteLoading ? 'Eliminando...' : 'Sí, Eliminar'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
